import { useMemo, useRef } from 'react';
import type { Market, PublishedEventListItem } from '../@types';

type TopMarketsByEvent = Record<string, Market[] | null>;

type CachedEvent = {
  source: PublishedEventListItem;
  topMarkets: Market[] | null | undefined;
  merged: PublishedEventListItem;
};

/**
 * Merges socket-owned top markets into fetched events while preserving every unchanged event
 * identity. This lets memoized event rows ignore updates addressed to another event.
 */
const useEventsWithTopMarkets = (events: PublishedEventListItem[], topMarkets: TopMarketsByEvent) => {
  const cacheRef = useRef(new Map<string, CachedEvent>());

  return useMemo(() => {
    const nextCache = new Map<string, CachedEvent>();

    const mergedEvents = events.map((event) => {
      const resolvedTopMarkets = topMarkets[event.id] ?? event.topMarkets;
      const cached = cacheRef.current.get(event.id);

      if (cached?.source === event && cached.topMarkets === resolvedTopMarkets) {
        nextCache.set(event.id, cached);
        return cached.merged;
      }

      const merged = resolvedTopMarkets === event.topMarkets ? event : { ...event, topMarkets: resolvedTopMarkets };
      nextCache.set(event.id, { source: event, topMarkets: resolvedTopMarkets, merged });
      return merged;
    });

    cacheRef.current = nextCache;
    return mergedEvents;
  }, [events, topMarkets]);
};

export default useEventsWithTopMarkets;
